import { useTranslation } from "@/hooks/useTranslation";
import { TranslationTree } from "@/translations";
import { Check } from "lucide-react";

type EnumType = { [key: string]: string | number };

interface BadgeSelectorProps<T extends EnumType> {
  selectedValues: T[keyof T][];
  enumObject: T;
  onChange: (selectedValues: T[keyof T][]) => void;
  maxSelections?: number;
  translation?: TranslationTree;
  valueToImageMap?: Partial<Record<T[keyof T], React.ReactNode>>;
}

function humanize(str: string): string {
  return str
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function enumToOptions<T extends EnumType>(
  enumObj: T
): { label: string; value: T[keyof T] }[] {
  return Object.entries(enumObj).map(([key, value]) => ({
    label: humanize(key),
    value: value as T[keyof T],
  }));
}

export default function BadgeSelector<T extends EnumType>({
  selectedValues,
  enumObject,
  onChange,
  maxSelections = 1,
  translation,
  valueToImageMap = {},
}: BadgeSelectorProps<T>) {
  const defaultTranslation = useTranslation();
  const t = translation ?? defaultTranslation;
  const options = enumToOptions(enumObject);

  const isValueSelected = (value: T[keyof T]) => selectedValues.includes(value);

  const handleSelect = (value: T[keyof T]) => {
    if (isValueSelected(value)) {
      onChange(selectedValues.filter((item) => item !== value));
    } else if (selectedValues.length < maxSelections) {
      onChange([...selectedValues, value]);
    } else {
      onChange([...selectedValues.slice(1), value]);
    }
  };

  const getTranslatedLabel = (value: T[keyof T]): string => {
    try {
      const translation =
        t.USER_FORM.ENUM_BADGES?.[
          value as keyof typeof t.USER_FORM.ENUM_BADGES
        ];

      if (translation && typeof translation === "string") {
        return translation;
      }
    } catch (e) {
      // If access fails, continue to fallback
    }
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : String(value);
  };

  return (
    <div className="flex flex-wrap gap-2 max-w-full">
      {options.slice(0, 5).map((option) => (
        <div
          key={String(option.value)}
          className={`
            inline-flex items-center rounded-full border border-transparent
            px-2 py-1 cursor-pointer transition-all font-normal text-sm
            ${isValueSelected(option.value) ? "bg-[#545454]" : "bg-[#303030]"}
            supports-hover:hover:bg-[#545454] gap-1
          `}
          onClick={() => handleSelect(option.value)}
        >
          {valueToImageMap[option.value] && (
            <div className="mr-1">{valueToImageMap[option.value]}</div>
          )}
          {getTranslatedLabel(option.value)}
          {isValueSelected(option.value) && (
            <Check className="h-4 w-4 inline" />
          )}
        </div>
      ))}
    </div>
  );
}
